// Copyright (c) Bentley Systems
import {
  Button,
  ButtonGroup,
  DefaultCell,
  IconButton,
  ProgressRadial,
  Table,
  tableFilters,
  TablePaginator,
  TablePaginatorRendererProps,
  Text,
  useToaster,
} from "@itwin/itwinui-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  customCellValueSort,
  FormatDate,
  getJsonFromParameters,
  getParametersFromUrlString,
  getQueryParam,
  getTotalHeightWithMargins,
  getTranslatedStringFromParameters,
  getUrlBasedOnEnv,
  setErrorState,
} from "../Helpers/helper-functions";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectAccessToken } from "../app/authorization-slice";
import { ArchiveVm } from "./Models/archive-vm";
import { parseMalwareScanCode } from "./download-malware";
import {
  selectMainMenuHeightWithMargin,
  selectSideMenuWidthWithMargin,
} from "../app/header-slice";
import Action from "../Action/Action";
import { DownloadFileActionButton } from "./Actions/DownloadFileAction/DownloadFileActionButton";
import { DeleteFileActionButton } from "./Actions/DeleteFileAction/DeleteFileActionButton";
import { ArchiveDocument } from "./Models/archive-document";
import { SvgDelete, SvgDownload } from "@itwin/itwinui-icons-react";
import { DocumentVersion } from "./Models/document-version";
import { setConfirmation } from "../app/confirmation-slice";
import { CellRendererProps } from "@itwin/itwinui-react/react-table";
import sanitize from "sanitize-filename";
import { MappingDto } from "./Models/mapping-dto";

const Archive = () => {
  const headerDivRef = useRef<HTMLDivElement>(null);
  const headerButtonsDivRef = useRef<HTMLDivElement>(null);
  const [headerDivHeight, setHeaderDivHeight] = useState<number>(0);
  const [headerButtonsDivHeight, setHeaderButtonsDivHeight] =
    useState<number>(0);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const sideMenuWidth = useAppSelector(selectSideMenuWidthWithMargin);
  const mainMenuHeight = useAppSelector(selectMainMenuHeightWithMargin);
  const [vm, setvm] = useState<ArchiveVm>();
  const [documents, setDocuments] = useState<ArchiveDocument[]>([]);
  const [selectedRows, setSelectedRows] = useState<Record<string, unknown>[]>(
    [],
  );
  const [isLoading, setLoading] = useState<boolean>(true);
  const abortControllerRef = useRef<AbortController | null>(null);
  const downloadAbortControllerRef = useRef<AbortController | null>(null);
  const bulkDownloadAbortControllerRef = useRef<AbortController | null>(null);
  const deleteAbortControllerRef = useRef<AbortController | null>(null);
  const bulkDeleteAbortControllerRef = useRef<AbortController | null>(null);
  const importMappingAbortControllerRef = useRef<AbortController | null>(null);
  const updateNamesAbortControllerRef = useRef<AbortController | null>(null);
  const { id, params } = useParams();
  const accessToken = useAppSelector(selectAccessToken);
  const urlParameters = useMemo(
    () => getParametersFromUrlString(params),
    [params],
  );
  const iTwinQueryParam = getQueryParam("iTwinId");

  useEffect(() => {
    if (!isLoading) {
      const calculateHeights = () => {
        setTimeout(() => {
          if (headerDivRef.current) {
            const element = headerDivRef.current;
            const totalHeight = getTotalHeightWithMargins(element);
            setHeaderDivHeight(totalHeight);
          }
          if (headerButtonsDivRef.current) {
            const element = headerButtonsDivRef.current;
            const totalHeight = getTotalHeightWithMargins(element);
            setHeaderButtonsDivHeight(totalHeight);
          }
        }, 0);
      };
      // Calculate heights initially
      calculateHeights();

      // Add resize event listener
      window.addEventListener("resize", calculateHeights);

      // Cleanup the event listener on unmount
      return () => {
        window.removeEventListener("resize", calculateHeights);
      };
    }
  }, [isLoading, vm?.caption]);

  const toaster = useToaster();

  const displayInformationalToast = (
    infoMsg: string,
    duration: number = 7000,
  ) => {
    if (infoMsg) {
      toaster.informational(infoMsg, {
        duration,
        hasCloseButton: true,
        type: "temporary",
      });
    }
  };

  const displayWarningToast = (
    infoMsg: string,
    persisting: boolean = false,
    duration: number = 7000,
  ) => {
    if (infoMsg) {
      toaster.warning(infoMsg, {
        duration,
        hasCloseButton: true,
        type: persisting ? "persisting" : "temporary",
      });
    }
  };

  const closeToaster = () => {
    toaster.closeAll();
  };

  const getFileBlob = async (
    docVersionId: number,
  ): Promise<Blob | undefined> => {
    const urlBlob = getUrlBasedOnEnv(
      `/api/Data/DownloadDocument/${ 
        encodeURIComponent(docVersionId.toString()) 
        }/${ 
        encodeURIComponent(id ?? "")}`,
    );
    const runId = crypto.randomUUID();

    // Create AbortController for this download
    downloadAbortControllerRef.current = new AbortController();

    const fetchResponse = await fetch(urlBlob, {
      method: "GET",
      headers: {
        Authorization: accessToken,
        RunId: runId,
        ...(iTwinQueryParam && { "Itwin-ID": iTwinQueryParam }),
      },
      signal: downloadAbortControllerRef.current.signal,
    })
      .then(async function (response: Response) {
        if (!response.ok) {
          if (response.status === 403) {
            const malwareCode = await parseMalwareScanCode(response);
            if (malwareCode === "NOT_SCANNED") {
              displayInformationalToast(
                "Document is not ready. Scanning for malwares. Please try again later.",
              );
            } else if (malwareCode === "MALWARE_FOUND") {
              displayWarningToast(
                `Document is not safe to download. Please contact support. ActivityId: ${ 
                  runId}`,
                true,
              );
            } else {
              setErrorState(response.status, dispatch, runId, true);
            }
          } else {
            setErrorState(response.status, dispatch, runId, true);
          }
          return undefined;
        } else {
          return response.blob();
        }
      })
      .catch((error: any) => {
        if (error?.name !== "AbortError") {
          setErrorState(500, dispatch, runId, true);
        }
        return undefined;
      })
      .finally(() => {});

    return fetchResponse;
  };

  const deleteFile = async (docVersionId: number): Promise<Response> => {
    const urlDel = getUrlBasedOnEnv(
      `/api/Data/DeleteDocument/${ 
        encodeURIComponent(docVersionId.toString()) 
        }/${ 
        encodeURIComponent(id ?? "")}`,
    );
    const runId = crypto.randomUUID();

    // Create AbortController for this delete operation
    deleteAbortControllerRef.current = new AbortController();

    const response = await fetch(urlDel, {
      method: "DELETE",
      headers: {
        Authorization: accessToken,
        RunId: runId,
        ...(iTwinQueryParam && { "Itwin-ID": iTwinQueryParam }),
      },
      signal: deleteAbortControllerRef.current.signal,
    }).catch((error: any) => {
      if (error?.name === "AbortError") {
        throw error; // Re-throw AbortError to be handled by caller
      }
      throw error;
    });
    return response;
  };
  const isBulkOperationDisabled = useMemo((): boolean => {
    return selectedRows.length === 0;
  }, [selectedRows]);

  // Both import and update user names buttons are enabled/disabled based on the same condition
  const isImportUpdateDisabled = useMemo((): boolean => {
    return selectedRows.length !== 1;
  }, [selectedRows]);

  function setDocumentsAfterDelete(docVersionIds: number[]) {
    if (vm) {
      docVersionIds.forEach((dvId) => {
        const i = vm.documents.findIndex((d) => d.version.id === dvId);
        vm.documents.splice(i, 1);
      });

      const newDocs: ArchiveDocument[] = [];
      vm.documents.forEach((doc) => {
        newDocs.push(
          new ArchiveDocument(
            doc.id,
            doc.nodeId,
            doc.name,
            doc.created,
            doc.version,
            doc.desc,
          ),
        );
      });
      setDocuments(newDocs);
      setSelectedRows([]);
    }
  }

  const deleteFiles = async (): Promise<void> => {
    displayInformationalToast("Deleting documents...");
    const docVersionIds: number[] = [];
    selectedRows.forEach((r) =>
      docVersionIds.push((r.Delete as DocumentVersion).id),
    );
    const urlDel = getUrlBasedOnEnv(
      `/api/Data/DeleteDocuments/${encodeURIComponent(id ?? "")}`,
    );
    const deleteFilesRunId = crypto.randomUUID();

    // Create AbortController for bulk delete
    bulkDeleteAbortControllerRef.current = new AbortController();

    await fetch(urlDel, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken,
        RunId: deleteFilesRunId,
        ...(iTwinQueryParam && { "Itwin-ID": iTwinQueryParam }),
      },
      body: JSON.stringify({ documentVersionIds: docVersionIds }),
      signal: bulkDeleteAbortControllerRef.current.signal,
    })
      .then(async function (response) {
        if (response.ok) {
          closeToaster();
          displayInformationalToast("Documents deleted");
          setDocumentsAfterDelete(docVersionIds);
        } else {
          const runId = response.headers.get("X-Run-Id") ?? deleteFilesRunId;
          setErrorState(response.status, dispatch, runId, true);
        }
      })
      .catch((error: any) => {
        if (error?.name !== "AbortError") {
          setErrorState(500, dispatch, deleteFilesRunId, true);
        }
      });
  };

  const downloadFiles = async (): Promise<{
    blob: Blob | undefined;
    filename: string | undefined;
  }> => {
    displayInformationalToast("Preparing items for download...");
    const docVersionIds: number[] = [];
    selectedRows.forEach((r) =>
      docVersionIds.push((r.Download as DocumentVersion).id),
    );
    const urlDownload = getUrlBasedOnEnv(
      `/api/Data/DownloadDocuments/${encodeURIComponent(id ?? "")}`,
    );
    const runId = crypto.randomUUID();

    // Create AbortController for bulk download
    bulkDownloadAbortControllerRef.current = new AbortController();

    const fetchResponse = await fetch(urlDownload, {
      method: "Post",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken,
        RunId: runId,
        ...(iTwinQueryParam && { "Itwin-ID": iTwinQueryParam }),
      },
      body: JSON.stringify({ documentVersionIds: docVersionIds }),
      signal: bulkDownloadAbortControllerRef.current.signal,
    })
      .then(async function (response: Response) {
        if (!response.ok) {
          if (response.status === 403) {
            const malwareCode = await parseMalwareScanCode(response);
            if (malwareCode === "NOT_SCANNED") {
              displayInformationalToast(
                "Document is not ready. Scanning for malwares. Please try again later.",
              );
            } else if (malwareCode === "MALWARE_FOUND") {
              displayWarningToast(
                `Document is not safe to download. Please contact support. ActivityId: ${ 
                  runId}`,
                true,
              );
            } else {
              setErrorState(response.status, dispatch, runId, true);
            }
          } else {
            setErrorState(response.status, dispatch, runId, true);
          }
        } else {
          //Get the Content-Disposition header
          const contentDisposition = response.headers.get(
            "Content-Disposition",
          );
          let filename = "files.zip"; // Default filename

          if (contentDisposition) {
            const match = contentDisposition.match(/filename="(.+)"/);
            if (match && match[1]) {
              filename = match[1];
            }
          }
          const blob = await response.blob();
          return { blob, filename };
        }
        return { blob: undefined, filename: undefined };
      })
      .catch((error: any) => {
        if (error?.name !== "AbortError") {
          setErrorState(500, dispatch, runId, true);
        }
        return { blob: undefined, filename: undefined };
      })
      .finally(() => {
        closeToaster();
      });
    return fetchResponse;
  };

  const onBulkDownload = async (): Promise<void> => {
    const blobWithName = await downloadFiles();
    if (
      blobWithName !== undefined &&
      blobWithName.blob !== undefined &&
      blobWithName.filename !== undefined
    ) {
      const fileURL = URL.createObjectURL(blobWithName.blob);

      const fileLink = document.createElement("a");
      fileLink.setAttribute("href", fileURL);
      const sanitizedFileName = sanitize(blobWithName.filename);
      fileLink.setAttribute("download", sanitizedFileName);
      document.body.appendChild(fileLink);
      fileLink.click();

      document.body.removeChild(fileLink);
      window.URL.revokeObjectURL(fileURL);
    }
  };

  useEffect(() => {
    abortControllerRef.current = new AbortController();
    toaster.setSettings({
      placement: "top",
      order: "descending",
    });
    setLoading(true);

    const jsonBody = getJsonFromParameters(urlParameters);
    const urlStructure = getUrlBasedOnEnv(
      `/api/Structure/GetNodeConfiguration/${encodeURIComponent(id ?? "")}`,
    );
    const confRunId = crypto.randomUUID();
    fetch(urlStructure, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken,
        RunId: confRunId,
        ...(iTwinQueryParam && { "Itwin-ID": iTwinQueryParam }),
      },
      body: jsonBody,
      signal: abortControllerRef.current.signal,
    })
      .then(async function (response) {
        if (!response.ok) {
          setErrorState(response.status, dispatch, confRunId, true);
        } else {
          return response.json();
        }
      })
      .then(function (data) {
        if (!data) {
          return;
        }
        const archiveVm = new ArchiveVm(data, urlParameters);
        setvm(archiveVm);
        setDocuments(archiveVm.documents);
        setLoading(false);
      })
      .catch((error: any) => {
        if (error?.name !== "AbortError") {
          setErrorState(500, dispatch, confRunId, true);
        }
      })
      .finally(() => setLoading(false));

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Also abort any ongoing downloads when component unmounts
      if (downloadAbortControllerRef.current) {
        downloadAbortControllerRef.current.abort();
      }
      // Also abort any ongoing bulk downloads when component unmounts
      if (bulkDownloadAbortControllerRef.current) {
        bulkDownloadAbortControllerRef.current.abort();
      }
      // Also abort any ongoing delete operations when component unmounts
      if (deleteAbortControllerRef.current) {
        deleteAbortControllerRef.current.abort();
      }
      // Also abort any ongoing bulk delete operations when component unmounts
      if (bulkDeleteAbortControllerRef.current) {
        bulkDeleteAbortControllerRef.current.abort();
      }
      // Also abort any ongoing import mapping operations when component unmounts
      if (importMappingAbortControllerRef.current) {
        importMappingAbortControllerRef.current.abort();
      }
      // Also abort any ongoing update names operations when component unmounts
      if (updateNamesAbortControllerRef.current) {
        updateNamesAbortControllerRef.current.abort();
      }
    };
  }, [accessToken, dispatch, id, params, toaster, urlParameters]);

  const emptyCell = <span className="placeholder-cell">--</span>;

  const getColumns = () => {
    const columns: any[] = [];
    const cellRenderer = (props: CellRendererProps<any>) => {
      return props.cellProps.value ? (
        <DefaultCell {...props}>{props.cellProps.value}</DefaultCell>
      ) : (
        <DefaultCell {...props}>{emptyCell}</DefaultCell>
      );
    };
    columns.push({
      Header: "Name",
      accessor: "Name",
      sortType: customCellValueSort,
      id: "Name",
      width: "25em",
      Filter: tableFilters.TextFilter(),
      cellRenderer,
    });
    columns.push({
      Header: "Description",
      accessor: "Description",
      sortType: customCellValueSort,
      id: "Description",
      width: "25em",
      Filter: tableFilters.TextFilter(),
      cellRenderer,
    });
    columns.push({
      Header: "Created",
      accessor: "Created",
      sortType: customCellValueSort,
      id: "Created",
      width: "15em",
      Filter: tableFilters.TextFilter(),
      cellRenderer: (props: CellRendererProps<any>) => {
        return props.cellProps.value ? (
          <DefaultCell {...props}>
            {FormatDate(props.cellProps.value)}
          </DefaultCell>
        ) : (
          <DefaultCell {...props}>{emptyCell}</DefaultCell>
        );
      },
    });

    columns.push({
      Header: "Download",
      accessor: "Download",
      id: "Download",
      width: "8em",
      disableSortBy: true,
      cellRenderer: (props: CellRendererProps<any>) => {
        return (
          <DefaultCell
            {...props}
            style={{
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DownloadFileActionButton
              apiDefinition={async () => {
                return getFileBlob(props.cellProps.value.id as number);
              }}
              onError={(responseStatusCode, activityId) => {
                setErrorState(responseStatusCode, dispatch, activityId, true);
              }}
              getFileName={() => {
                return props.cellProps.value.originalFileName as string;
              }}
            />
          </DefaultCell>
        );
      },
    });
    columns.push({
      Header: "Delete",
      accessor: "Delete",
      id: "Delete",
      width: "8em",
      disableSortBy: true,
      cellRenderer: (props: CellRendererProps<any>) => {
        return (
          <DefaultCell
            {...props}
            style={{
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DeleteFileActionButton
              apiDefinition={async () => {
                return deleteFile(props.cellProps.value.id as number);
              }}
              setToasterDeletedSuccess={(infoMsg, docVersionId) => {
                displayInformationalToast(infoMsg);
                const docVersionArr: number[] = [];
                docVersionArr.push(docVersionId);
                setDocumentsAfterDelete(docVersionArr);
              }}
              onError={(responseStatusCode, activityId) => {
                setErrorState(responseStatusCode, dispatch, activityId, true);
              }}
              getDocVersionId={() => {
                return props.cellProps.value.id as number;
              }}
            />
          </DefaultCell>
        );
      },
    });
    return columns;
  };

  const pageSizeList = useMemo(() => [10, 25, 50, 100], []);
  const paginator = useCallback(
    (props: TablePaginatorRendererProps) => (
      <TablePaginator
        {...props}
        pageSizeList={pageSizeList}
        isLoading={false}
      />
    ),
    [pageSizeList],
  );

  const getData = useMemo(() => {
    const returnValues: Record<string, unknown>[] = [];
    for (const doc of documents) {
      returnValues.push({
        Name: doc.name,
        Description: doc.desc,
        Created: doc.created.toLocaleString(),
        Download: doc.version,
        Delete: doc.version,
      });
    }
    return returnValues;
  }, [documents]);

  function handleAddClick() {
    let navigateStr = `/archiveadd/${  id  }/${  params}`;
    navigateStr = navigateStr + window.location.search;
    void navigate(navigateStr);
  }

  function getHeader(): string | undefined {
    if (vm !== undefined && vm.caption !== undefined && vm.caption !== null) {
      return getTranslatedStringFromParameters(vm.caption, urlParameters);
    } else {
      return vm?.caption;
    }
  }

  function getHeaderBackAction(): React.JSX.Element | undefined {
    if (vm?.headerBackAction !== undefined) {
      return (
        <Action
          action={vm.headerBackAction}
          key={vm.headerBackAction.actionId}
        />
      );
    }
  }

  const getEnhancedAction = (action: any) => {
    if (action.caption === "Import excel") {
      return new Proxy(action, {
        get(target, prop) {
          if (prop === "executeAction") {
            return async () => {
              await handleImportMapping();
            };
          }
          return target[prop];
        },
      });
    }
    if (action.caption === "Update user names") {
      return new Proxy(action, {
        get(target, prop) {
          if (prop === "executeAction") {
            return async () => {
              await handleUpdateUserNames();
            };
          }
          return target[prop];
        },
      });
    }
    return action;
  };

  const headerButtons = vm?.isMapping
    ? vm?.headerActions?.map((action) => {
        const enhancedAction = getEnhancedAction(action);
        const disabled =
          action.caption === "Import excel" ||
          action.caption === "Update user names"
            ? isImportUpdateDisabled
            : undefined;
        return (
          <Action
            action={enhancedAction}
            key={action.actionId}
            disabled={disabled}
          />
        );
      })
    : vm?.headerActions?.map((action) => {
        return <Action action={action} key={action.actionId} />;
      });

  const onRowsSelect = useCallback((selected: any) => {
    // Defer to avoid "setState during render" — iTwinUI Table (react-table) invokes
    // onSelect during its own render phase, which would update Archive mid-render.
    setTimeout(() => setSelectedRows(selected ?? []), 0);
  }, []);

  async function onBulkDelete(): Promise<void> {
    dispatch(
      setConfirmation({
        isConfirmationOpen: true,
        onOkAction: async () => {
          await deleteFiles();
        },
        caption: "Delete documents?",
        text: `Do you really want to delete documents?`,
      }),
    );
  }

  function getMappingDto(): MappingDto | null {
    const imodelId = urlParameters.find(
      (param) => param.name && param.name.toLowerCase() === "imodelid",
    )?.value;

    if (!imodelId) {
      return null;
    }

    const docVersionId = (selectedRows[0].Download as DocumentVersion).id;
    const mappingDto: MappingDto = {
      documentVersionId: docVersionId,
      archiveNodeId: Number(id),
      iModelId: imodelId,
    };

    return mappingDto;
  }

  async function handleImportMapping(): Promise<void> {
    const mappingDto = getMappingDto();

    if (!mappingDto) {
      displayWarningToast("iModelId is required to import mapping.");
      return;
    }

    const urlImportMapping = getUrlBasedOnEnv(`/api/Mapping/ImportNames`);
    const runId = crypto.randomUUID();

    displayInformationalToast(
      "Importing mapping. If successful, the document will be deleted.",
    );

    // Create AbortController for import mapping operation
    importMappingAbortControllerRef.current = new AbortController();

    try {
      const response = await fetch(urlImportMapping, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken,
          RunId: runId,
        },
        body: JSON.stringify(mappingDto),
        signal: importMappingAbortControllerRef.current.signal,
      });

      if (!response.ok) {
        setErrorState(response.status, dispatch, runId, true);
        return;
      }
      const data = await response.json();
      displayInformationalToast(
        `Import mapping completed successfully. ${data.inserted} rows inserted.`,
      );
      if (data.deleteSucceeded) {
        const docVersionArr: number[] = [];
        docVersionArr.push(mappingDto.documentVersionId);
        setDocumentsAfterDelete(docVersionArr);
      } else {
        displayWarningToast(
          "Failed to delete document. Please delete it manually.",
        );
      }
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        setErrorState(500, dispatch, runId, true);
      }
    }
  }

  async function handleUpdateUserNames(): Promise<void> {
    const mappingDto = getMappingDto();

    if (!mappingDto) {
      displayWarningToast("iModelId is required to update user names.");
      return;
    }

    const urlUpdateUserNames = getUrlBasedOnEnv(`/api/Mapping/UpdateNames`);
    const runId = crypto.randomUUID();

    displayInformationalToast(
      "Updating user names. If successful, the document will be deleted.",
    );

    // Create AbortController for update names operation
    updateNamesAbortControllerRef.current = new AbortController();

    try {
      const response = await fetch(urlUpdateUserNames, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken,
          RunId: runId,
        },
        body: JSON.stringify(mappingDto),
        signal: updateNamesAbortControllerRef.current.signal,
      });

      if (!response.ok) {
        setErrorState(response.status, dispatch, runId, true);
        return;
      }

      const data = await response.json();
      displayInformationalToast(
        `Update user names completed successfully. ${data.updated} user names updated.`,
      );
      if (data.deleteSucceeded) {
        const docVersionArr: number[] = [];
        docVersionArr.push(mappingDto.documentVersionId);
        setDocumentsAfterDelete(docVersionArr);
      } else {
        displayWarningToast(
          "Failed to delete document. Please delete it manually.",
        );
      }
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        setErrorState(500, dispatch, runId, true);
      }
    }
  }

  return (
    <div style={{ height: "100%" }}>
      {isLoading ? (
        <div
          className="becs-ltr-margin"
          style={{ display: "flex", justifyContent: "center" }}
        >
          <ProgressRadial indeterminate />
        </div>
      ) : (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div>
            <div className="becs-infopanelbody-footer-line">
              <div className="becs-all-margin" ref={headerDivRef}>
                <div className="rowWrap">
                  {getHeaderBackAction()}
                  <Text variant="title">{getHeader()}</Text>
                </div>
              </div>
            </div>
            <div className="becs-ltr-margin">
              <div
                className="horizontal becs-tb-margin"
                ref={headerButtonsDivRef}
              >
                <div className="leftAlign">
                  <div className="rowWrap">
                    <Button styleType="default" onClick={handleAddClick}>
                      Add new document
                    </Button>
                    {headerButtons}
                    <ButtonGroup role="toolbar">
                      <IconButton
                        label="Delete"
                        onClick={onBulkDelete}
                        disabled={isBulkOperationDisabled}
                      >
                        <SvgDelete />
                      </IconButton>
                      <IconButton
                        label="Download"
                        onClick={onBulkDownload}
                        disabled={isBulkOperationDisabled}
                      >
                        <SvgDownload />
                      </IconButton>
                    </ButtonGroup>
                  </div>
                </div>
              </div>
              <Table
                columns={getColumns()}
                emptyTableContent="No data."
                data={getData}
                isLoading={isLoading}
                style={{
                  height: `calc(100vh - ${mainMenuHeight + headerDivHeight + headerButtonsDivHeight}px)`,
                  maxWidth: `calc(100vw - ${sideMenuWidth}px)`,
                }}
                isSortable={true}
                isResizable={false}
                density="extra-condensed"
                styleType="zebra-rows"
                paginatorRenderer={paginator}
                pageSize={50}
                isSelectable={true}
                onSelect={onRowsSelect}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Archive;
